import { AgGridReact } from '@ag-grid-community/react';
import type { MutableRefObject } from 'react';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Subscription } from 'rxjs';
import type { GridReadyEvent, IGetRowsParams } from '@ag-grid-community/core';

import { AttrsHelper } from '../../../sb-helpers/attrs-helper';
import { DesignSysEnvUtil } from '../../../utils/design-sys-env-util';

import { DataGridPinnedUtil } from './data-grid-pinned-util';
import type { InternalDataGridProps, LazyLoadGridApi } from './data-grid.types';

export const DataGridInfinite = React.forwardRef<
  LazyLoadGridApi,
  InternalDataGridProps
>(
  (
    {
      className,
      columns,
      dataSource,
      name,
      noResultsComponent,
      noRowsOverlayComponentParams,
      getContextMenuItems,
      onColumnMoved,
      onInternalCellClicked,
      onSelectionChanged,
      sorting,
      allowContextMenu = false,
      tooltipShowDelay,
      tooltipShowMode,
      tooltipInteraction,
    },
    ref
  ) => {
    const gridRef = useRef<LazyLoadGridApi>(null);
    // Passes ref to the parent
    useImperativeHandle(ref, () => {
      return {
        api: gridRef.current?.api,
      };
    });

    const [gridReady, setGridReady] = useState(false);
    const unsubscribeListener = useRef<Subscription>();
    const isSettingDatasourceRef = useRef(false);
    const hasPendingDatasourceResetRef = useRef(false);
    const hasGridReadyFiredRef = useRef(false);
    const isInitialMountRef = useRef(true);
    const datasourceFactoryRef = useRef<
      () => { getRows: (params: IGetRowsParams) => void }
    >(() => ({
      getRows: () => {},
    }));

    const queueDatasourceReset = useCallback(() => {
      if (!gridRef.current?.api) {
        return;
      }

      if (isSettingDatasourceRef.current) {
        hasPendingDatasourceResetRef.current = true;
        return;
      }

      isSettingDatasourceRef.current = true;
      gridRef.current.api.setGridOption(
        'datasource',
        datasourceFactoryRef.current()
      );
      setTimeout(() => {
        isSettingDatasourceRef.current = false;
        if (hasPendingDatasourceResetRef.current) {
          hasPendingDatasourceResetRef.current = false;
          queueDatasourceReset();
        }
      }, 0);
    }, []);

    const fallbackToNoRows = (params: IGetRowsParams) => {
      params.successCallback([], 0);
      gridRef.current?.api?.setGridOption(
        'noRowsOverlayComponentParams',
        noRowsOverlayComponentParams
      );
      setTimeout(() => {
        gridRef.current?.api?.showNoRowsOverlay();
      }, 0);
      setGridReady(true);
    };

    const updateGrid = (
      response: unknown,
      params: IGetRowsParams,
      source: NonNullable<InternalDataGridProps['dataSource']>
    ) => {
      try {
        const parsedResults = source.parseData(response);

        // Genuinely empty dataset — show the no-rows overlay.
        if (parsedResults.totalPages === 0) {
          fallbackToNoRows(params);
          return;
        }

        const data = parsedResults.data ?? [];

        // Over-fetch past the end (ag-grid's infinite row model can ask
        // for a page beyond totalPages during pre-fetch / refresh races).
        // Tell ag-grid the lastRow without resetting the row count to 0,
        // otherwise the grid drops its cached rows and appears empty.
        if (
          data.length === 0 &&
          parsedResults.currentPage > parsedResults.totalPages
        ) {
          params.successCallback(
            [],
            parsedResults.totalPages * source.getPageSize()
          );
          return;
        }

        gridRef.current?.api?.hideOverlay();
        params.successCallback(
          data,
          parsedResults.totalPages === parsedResults.currentPage
            ? (parsedResults.totalPages - 1) * source.getPageSize() +
                data.length
            : -1
        );
        setGridReady(true);
      } catch {
        fallbackToNoRows(params);
      }
    };
    const constructDataSource = () => ({
      getRows: (params: IGetRowsParams) => {
        if (dataSource) {
          const askingPage =
            Math.floor(params.startRow / dataSource.getPageSize()) + 1;
          void dataSource
            .getData({
              sort: sorting,
              page: askingPage,
            })
            .then(response => {
              if (!unsubscribeListener.current) {
                unsubscribeListener.current = dataSource.onInvalidate(() => {
                  // When data is invalidated, reset the datasource to start from page 1
                  // The useEffect won't interfere because dataSource/sorting props haven't changed
                  if (gridRef.current?.api) {
                    // Unsubscribe from current listener and clear ref so new datasource can set up its own subscription
                    const currentSubscription = unsubscribeListener.current;
                    unsubscribeListener.current = undefined;
                    if (currentSubscription) {
                      currentSubscription.unsubscribe();
                    }
                    // Purge the infinite cache before setting new datasource to prevent stale block requests
                    gridRef.current.api.purgeInfiniteCache();
                    // Hide overlay to force fresh render with updated params
                    gridRef.current.api.hideOverlay();
                    queueDatasourceReset();
                  }
                });
              }
              updateGrid(response, params, dataSource);
            })
            .catch(() => {
              fallbackToNoRows(params);
            });
        }
      },
    });
    datasourceFactoryRef.current = constructDataSource;

    const onGridReady = useCallback(
      (readyParams: GridReadyEvent<unknown>) => {
        isSettingDatasourceRef.current = true;
        readyParams.api.setGridOption(
          'datasource',
          datasourceFactoryRef.current()
        );
        hasGridReadyFiredRef.current = true;
        // Allow useEffect to run after parent's initial effects complete
        setTimeout(() => {
          isSettingDatasourceRef.current = false;
          if (hasPendingDatasourceResetRef.current) {
            hasPendingDatasourceResetRef.current = false;
            queueDatasourceReset();
          }
        }, 0);
      },
      [queueDatasourceReset]
    );

    useEffect(() => {
      // Skip effect on initial mount - onGridReady will set the datasource
      if (isInitialMountRef.current) {
        isInitialMountRef.current = false;
        return;
      }

      // Only update datasource if grid has been initialized (onGridReady has fired).
      // If a datasource reset is already in progress, queueDatasourceReset will
      // queue this request and process it after the current one completes.
      if (hasGridReadyFiredRef.current) {
        queueDatasourceReset();
      }

      return () => {
        if (unsubscribeListener.current) {
          unsubscribeListener.current.unsubscribe();
          unsubscribeListener.current = undefined;
        }
        hasPendingDatasourceResetRef.current = false;
      };
    }, [dataSource, sorting, queueDatasourceReset]);

    // Only call cleanup on actual component unmount, not on dataSource swap
    const dataSourceRef = useRef(dataSource);
    dataSourceRef.current = dataSource;
    useEffect(() => {
      return () => {
        dataSourceRef.current?.cleanup();
      };
    }, []);

    const defaultColDef = useMemo(() => {
      return {
        autoHeight: !dataSource,
        sortable: false,
      };
    }, [dataSource]);

    return (
      <div
        className={AttrsHelper.formatClassname(
          className,
          'ag-theme-quartz ag-theme-axiom theme-infinite'
        )}
        data-test={name}
        data-ready={gridReady}
      >
        <AgGridReact
          enableCellTextSelection
          suppressRowVirtualisation={
            DesignSysEnvUtil.suppressGridRowVirtualization
          }
          ref={gridRef as MutableRefObject<AgGridReact<unknown>>}
          suppressContextMenu={!allowContextMenu}
          getContextMenuItems={getContextMenuItems}
          allowContextMenuWithControlKey
          columnDefs={columns}
          defaultColDef={defaultColDef}
          rowBuffer={0}
          rowSelection="single"
          rowModelType="infinite"
          cacheBlockSize={dataSource?.getPageSize()}
          onColumnMoved={onColumnMoved}
          suppressDragLeaveHidesColumns
          maxConcurrentDatasourceRequests={1}
          noRowsOverlayComponent={noResultsComponent}
          noRowsOverlayComponentParams={noRowsOverlayComponentParams}
          processUnpinnedColumns={DataGridPinnedUtil.unpinnable}
          onGridReady={onGridReady}
          suppressCellFocus
          onCellClicked={onInternalCellClicked}
          onSelectionChanged={() => {
            onSelectionChanged();
          }}
          tooltipShowDelay={tooltipShowDelay}
          tooltipShowMode={tooltipShowMode}
          tooltipInteraction={tooltipInteraction}
        />
      </div>
    );
  }
);
